/** Instagram の接続方法: プロアカウントにして、Meta for Developers のアプリダッシュボードで長期トークンを発行して貼る */
import {
  BrowserFrame, Callout, Checklist, Code, Mark, MockButton, MockInput, MockRow, MockSidebarLayout,
  PhoneFrame, PostScreen, SectionTitle, Step, Steps, Trouble, UiLabel,
} from './parts'
import { ChannelRowMock, ConnectButtonsMock, ConnectFormMock } from './postMocks'

export function InstagramGuide() {
  return (
    <div className="space-y-5">
      <Callout kind="info" title="Instagram も「長期アクセストークン」を1つ貼るだけです">
        <p>
          Instagram の <b>プロアカウント</b>（ビジネスかクリエイター）にしてから、Meta の開発者サイトで自分専用のアプリを作り、トークンを発行します。
          自分のアカウントにだけ使うので、Meta の審査は要りません。トークンは 60 日有効で、<b>期限が近づくと Genemo Post が自動で延長</b>します。
        </p>
      </Callout>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="gm-card space-y-2 p-4">
          <h3 className="text-sm font-bold">準備するもの</h3>
          <Checklist items={[
            '投稿したい Instagram アカウント（プロアカウントに切り替えます。無料）',
            'Meta for Developers に登録した Facebook アカウント（初めてなら登録します。無料）',
            'Facebook ページとのリンクは不要です',
            '所要時間の目安: 20分',
          ]} />
        </div>
        <div className="gm-card space-y-2 p-4">
          <h3 className="text-sm font-bold">知っておくこと</h3>
          <Checklist items={[
            <><b>画像が必須</b>です。Genemo Post の設定で画像の生成をオンにします（手順6）。</>,
            '画像は横長〜正方形〜縦 4:5 まで。縦長のコマ割り（9:16）は Instagram に出せません。',
            'キャプションは 2,200 字・ハッシュタグ 30 個まで。本文は X 向けに作った文を使います。',
            '1 アカウントにつき 24 時間で 100 件まで投稿できます（Instagram の制限）。',
          ]} />
        </div>
      </div>

      <Steps>
        <Step n={1} title="Instagram をプロアカウントに切り替える"
          mock={
            <PhoneFrame title="アカウントの種類とツール">
              <MockRow><span>認証済みバッジ</span></MockRow>
              <Mark n={1} block><MockRow><span className="font-bold">プロアカウントに切り替える</span><span className="ml-auto">›</span></MockRow></Mark>
              <div className="pt-2 text-[11px] text-ink-muted">どちらを選んでも Genemo Post から投稿できます</div>
              <div className="grid grid-cols-2 gap-1.5">
                <MockRow><span>● ビジネス</span></MockRow>
                <MockRow><span>○ クリエイター</span></MockRow>
              </div>
            </PhoneFrame>
          }>
          <p>
            すでにビジネスかクリエイターのアカウントなら、この手順は飛ばしてください。
          </p>
          <p>
            Instagram アプリで、プロフィール → 右上の <UiLabel>≡</UiLabel> → <UiLabel en="Account type and tools">アカウントの種類とツール</UiLabel> →
            <UiLabel en="Switch to professional account">プロアカウントに切り替える</UiLabel> を選び、カテゴリを選んで <b>ビジネス</b> か <b>クリエイター</b> を選びます。
          </p>
          <Callout kind="tip">お店のアカウントなら「ビジネス」がおすすめです。切り替えても、フォロワーや投稿はそのまま残ります。</Callout>
        </Step>

        <Step n={2} title="Meta for Developers でアプリを作る"
          mock={
            <BrowserFrame url="developers.facebook.com/apps/creation">
              <div className="space-y-2">
                <div className="font-bold">アプリを作成</div>
                <MockInput label="アプリ名" value="genemo-post-instagram" />
                <div className="text-[11px] text-ink-muted">ユースケースを追加</div>
                <MockRow><span>Threads API にアクセス</span><span className="ml-auto">☐</span></MockRow>
                <Mark n={2} block><MockRow><span className="font-bold">Instagram でメッセージとコンテンツを管理</span><span className="ml-auto">☑</span></MockRow></Mark>
                <div className="flex justify-end"><MockButton kind="primary">次へ</MockButton></div>
              </div>
            </BrowserFrame>
          }>
          <p>
            <Code>developers.facebook.com/apps</Code> を開き、<UiLabel en="Create App">アプリを作成</UiLabel> を押します。
          </p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>アプリ名（自分で分かれば何でも可）と連絡先メールを入れます。</li>
            <li>ユースケースで <UiLabel en="Manage messaging & content on Instagram">Instagram でメッセージとコンテンツを管理</UiLabel> にチェックを入れます。</li>
            <li>ビジネスポートフォリオは <UiLabel>まだ接続しない</UiLabel> で構いません。アプリの種類を聞かれたら <UiLabel en="Business">ビジネス</UiLabel> を選びます。</li>
            <li>確認画面で <UiLabel en="Create app">アプリを作成</UiLabel> を押します。</li>
          </ol>
          <Callout kind="tip">Threads 用に作ったアプリとは別のアプリにすると、あとで分かりやすいです。</Callout>
        </Step>

        <Step n={3} title="「Instagram ログインによる API 設定」を開き、権限を確かめる"
          mock={
            <BrowserFrame url="developers.facebook.com/apps/…/instagram-business/API-Setup">
              <MockSidebarLayout menu={['アクセス許可と機能', 'Instagram ログインによる API 設定', 'Facebook ログインによる API 設定']} active="Instagram ログインによる API 設定">
                <div className="font-bold">Instagram ログインによる API 設定</div>
                <div className="text-[11px] text-ink-muted">アクセス許可</div>
                <Mark n={3} block>
                  <div className="space-y-1 p-1">
                    <MockRow><Code>instagram_business_basic</Code><span className="ml-auto text-ok">✓</span></MockRow>
                    <MockRow><Code>instagram_business_content_publish</Code><span className="ml-auto text-ok">✓</span></MockRow>
                  </div>
                </Mark>
              </MockSidebarLayout>
            </BrowserFrame>
          }>
          <p>
            左のメニューの <UiLabel en="Use cases">ユースケース</UiLabel> → <b>Instagram でメッセージとコンテンツを管理</b> の <UiLabel en="Customize">カスタマイズ</UiLabel> を開き、
            <UiLabel en="API setup with Instagram login">Instagram ログインによる API 設定</UiLabel> を選びます。
          </p>
          <p>
            <UiLabel en="Permissions and features">アクセス許可と機能</UiLabel> に、<Code>instagram_business_basic</Code> と <Code>instagram_business_content_publish</Code> が入っていることを確かめます。
            無ければ <UiLabel en="Add">追加</UiLabel> を押します。
          </p>
          <Callout kind="warn">
            <b>「Facebook ログインによる API 設定」ではありません。</b> 間違えると、Genemo Post では使えないトークンになります。
          </Callout>
        </Step>

        <Step n={4} title="自分の Instagram アカウントを追加し、トークンを発行する"
          mock={
            <BrowserFrame url="developers.facebook.com/apps/…/instagram-business/API-Setup">
              <div className="space-y-2">
                <div className="font-bold">1. アクセストークンを生成する</div>
                <div className="flex justify-end"><Mark n={4}><MockButton>アカウントを追加</MockButton></Mark></div>
                <MockRow>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-2 text-[10px] font-bold">G</span>
                  <span>genemo_coffee</span>
                  <span className="text-[11px] text-ink-muted">ID 1784…</span>
                  <span className="ml-auto"><Mark n={5}><MockButton>トークンを生成</MockButton></Mark></span>
                </MockRow>
                <div className="space-y-1.5 rounded-md border border-line-strong p-2 shadow-sm">
                  <div className="font-bold">アクセストークン</div>
                  <div className="truncate rounded bg-surface-2 px-2 py-1 font-mono text-[11px]">IGAA…（長い英数字）…</div>
                  <div>☑ 理解しました</div>
                  <div className="flex justify-end"><Mark n={6}><MockButton kind="primary">コピー</MockButton></Mark></div>
                </div>
              </div>
            </BrowserFrame>
          }>
          <p>同じ画面の <UiLabel en="Generate access tokens">1. アクセストークンを生成する</UiLabel> で操作します。</p>
          <ol className="list-decimal space-y-1 pl-5">
            <li><UiLabel en="Add account">アカウントを追加</UiLabel> を押し、投稿したい Instagram アカウントでログインして許可します。</li>
            <li>一覧に出たアカウントの <UiLabel en="Generate token">トークンを生成</UiLabel> を押し、もう一度ログイン・許可します。</li>
            <li>表示されたトークンを <UiLabel en="Copy">コピー</UiLabel> します（確認のチェックを求められたら入れます）。</li>
          </ol>
          <Callout kind="tip" title="このトークンはそのまま使えます">
            アプリダッシュボードで作ったトークンは、最初から <b>60日有効の長期トークン</b> です（Meta の公式説明）。交換の作業は要りません。
          </Callout>
          <Callout kind="warn" title="テスターの招待を求められたとき">
            「招待を承認してください」と出たり、一覧にアカウントが出なかったりする場合は、Instagram の <UiLabel en="Settings">設定</UiLabel> →
            <UiLabel en="Website permissions">ウェブサイトのアクセス許可</UiLabel> → <UiLabel en="Apps and websites">アプリとウェブサイト</UiLabel> の
            <UiLabel en="Tester invites">テスターへの招待</UiLabel> で、作ったアプリを承認してからやり直してください。
          </Callout>
          <Callout kind="secret">トークンは、アカウントに投稿できる鍵です。他人に見せたり、チャットやメールに貼ったりしないでください。</Callout>
        </Step>

        <Step n={5} title="Genemo Post に貼って接続する"
          mock={
            <div className="space-y-3">
              <ConnectButtonsMock highlight="Instagram を接続（トークン）" n={7} />
              <ConnectFormMock n={8} note="Instagram のプロアカウント（ビジネスかクリエイター）が必要です。…長期アクセストークン（60日有効）を貼ります。…"
                fields={[{ label: '長期アクセストークン', value: 'IGAAxxxxxxxxxxxxxxxxxxxxxx', secret: true }]} />
              <ChannelRowMock platform="Instagram" handle="genemo_coffee" extra="トークンを入れ直す" />
            </div>
          }>
          <p>
            Genemo Post の <UiLabel>ペルソナ</UiLabel> で、投稿させたいペルソナの <UiLabel>Instagram を接続（トークン）</UiLabel> を押します。
          </p>
          <p>
            <UiLabel>長期アクセストークン</UiLabel> の欄に貼り、<UiLabel>接続する</UiLabel> を押します。
            Genemo Post が Instagram に問い合わせて、持ち主と投稿の権限をその場で確かめ、トークンの期限も延長しておきます。
          </p>
          <p>Instagram の行に <span className="gm-chip gm-chip-ok">接続中</span> と、ユーザー名が出れば接続は完了です。続けて手順6の画像の設定をしてください。</p>
        </Step>

        <Step n={6} title="画像の設定を Instagram 向けにする"
          mock={
            <PostScreen title="/settings">
              <div className="mb-2 flex gap-3 border-b border-line pb-1.5 text-[12px]">
                <span className="text-ink-muted">執筆スタイル</span><span className="border-b-2 border-primary font-bold">画像生成</span>
                <span className="text-ink-muted">通知</span><span className="text-ink-muted">安全チェック</span>
              </div>
              <div className="space-y-2">
                <div className="rounded-md border border-line p-2">
                  <div className="font-bold">投稿画像の自動生成</div>
                  <div className="mt-1"><Mark n={9}><span className="px-1">☑ 投稿に画像を付ける</span></Mark></div>
                </div>
                <div className="rounded-md border border-line p-2">
                  <div className="font-bold">コマ割り・4コマモード</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className="text-ink-muted">発動確率</span>
                    <Mark n={10}><span className="gm-num px-1 font-bold">0%</span></Mark>
                    <span className="text-ink-muted">または形を</span>
                    <Mark n={11}><span className="px-1 font-bold">四角（2×2の4コマ）</span></Mark>
                  </div>
                </div>
              </div>
            </PostScreen>
          }>
          <p>Instagram は画像のない投稿を受け付けません。Genemo Post の <UiLabel>設定</UiLabel> → <UiLabel>画像生成</UiLabel> を開きます。</p>
          <ul className="list-disc space-y-1 pl-5">
            <li><UiLabel>投稿に画像を付ける</UiLabel> をオンにします（画像1枚につき約 1.5cr、コマ割りの四角は約 3cr かかります）。</li>
            <li>
              <b>コマ割り・4コマモード</b> を使う場合は、形を <UiLabel>四角（2×2の4コマ）</UiLabel> にします。
              <UiLabel>縦型（縦に並ぶ）</UiLabel> は 9:16 で Instagram に出せないので、使うなら発動確率を <b>0%</b> にします。
            </li>
          </ul>
          <Callout kind="info">
            設定が合っていないと、<UiLabel>投稿を作る</UiLabel> の画面で Instagram を選んだときに案内が出て、生成ボタンが押せなくなります。
          </Callout>
        </Step>

        <Step n={7} title="投稿後に「投稿済み（要確認）」が出たら"
          mock={
            <PostScreen title="/queue">
              <div className="space-y-1.5">
                <div><span className="gm-chip gm-chip-warn">投稿済み（要確認）</span></div>
                <div className="rounded-md bg-warn-bg p-2">
                  <div className="font-bold text-warn">投稿済み（要確認）</div>
                  <div className="mt-1">Instagram に投稿されましたが、キャプション（本文）が付きませんでした。投稿を開いて、本文を手で追加してください</div>
                  <div className="mt-1.5 flex gap-1.5"><MockButton>投稿を開く ↗</MockButton><Mark n={12}><MockButton>確認しました</MockButton></Mark></div>
                </div>
              </div>
            </PostScreen>
          }>
          <p>
            まれに、Instagram 側で「画像は公開されたが、キャプションが付かなかった」ことがあります。
            このとき承認キューに <b>投稿済み（要確認）</b> として残ります。
          </p>
          <p><UiLabel>投稿を開く</UiLabel> から Instagram の投稿を開き、キャプションを編集して足してから、<UiLabel>確認しました</UiLabel> を押してください。</p>
        </Step>
      </Steps>

      <SectionTitle>うまくいかないとき</SectionTitle>
      <Trouble items={[
        { symptom: '「このトークンには Instagram に投稿する権限がありません」', fix: '手順3の権限（instagram_business_content_publish）が入っていません。追加してから、手順4でトークンを作り直して貼ってください。' },
        { symptom: '「Instagram のトークンを確認できませんでした」「トークンが無効です」', fix: 'コピーの途中で切れているか、「Facebook ログインによる API 設定」のトークンを貼っています。手順3〜4の画面で作り直してください。' },
        { symptom: '接続できたのに、翌日「認証エラー」になった', fix: '1時間だけ有効な短期トークンを貼った可能性があります。手順4のアプリダッシュボードで作ったトークンを、行の「トークンを入れ直す」から貼ってください。' },
        { symptom: '「Instagram に出すには画像が必要です」「縦横比が範囲の外です」', fix: '手順6の画像の設定を直してから、承認キューの「作り直す」で投稿を作り直してください。' },
        { symptom: '「投稿の権限を確認できませんでした。時間をおいてやり直してください」', fix: 'Instagram 側が混み合っています。数分おいてからもう一度「接続する」を押してください。' },
      ]} />
    </div>
  )
}
